import React, { useState, useEffect, useCallback } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { useNavigate } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';

import GridItem from 'components/Grid/GridItem.js';
import AuthenticatedContainer from 'components/AuthenticatedContainer';
import GavelIcon from '@mui/icons-material/Gavel';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';

import EVChargingService from '../../services/EVChargingService';
import useAPI from 'useAPI';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    maxWidth: 360,
    backgroundColor: theme.palette.background.paper,
    position: 'relative',
    overflow: 'auto',
    color: '#324152',
    marginTop: 20,
    marginBottom: 80,
  },
}));

function formatBrand(brand) {
  if (!brand) return '';
  return brand
    .split(/[ _-]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

// Status chip props for the TOS list row. Returns null when state is
// unknown (loading) so the chip is omitted entirely.
function tosChipProps(enrollment) {
  if (!enrollment) return null;
  if (enrollment.tos_accepted_at) {
    return { label: 'Accepted', color: 'success' };
  }
  return { label: 'Required', color: 'warning' };
}

// Status chip props for the Payment Method list row. Driven entirely from
// the existing getEnrollment response shape — no Stripe round-trip. Future
// states ('Expired', 'Charge failed') are deferred to follow-up tickets.
function paymentChipProps(enrollment) {
  if (!enrollment) return null;
  if (enrollment.status === 'cancelled') {
    return { label: 'Cancelled', color: 'default' };
  }
  if (enrollment.status === 'paused') {
    return { label: 'Paused', color: 'warning' };
  }
  if (enrollment.card && enrollment.card.brand && enrollment.card.last4) {
    return {
      label: `${formatBrand(enrollment.card.brand)} •• ${enrollment.card.last4}`,
      color: 'success',
    };
  }
  return { label: 'Not added', color: 'warning' };
}

// Status chip props for the Wallet list row. Driven by the wallet
// shape returned by GET /ev-charging/wallet:
//   - latest top-up failed   → error (red, "Top-up failed") — trumps balance
//   - balance >= threshold   → success (green)
//   - balance below threshold → warning (amber)
//   - no wallet yet (null)   → null (chip omitted; menu row hidden anyway)
function walletChipProps(wallet) {
  if (!wallet || !wallet.wallet) return null;
  const latestTopUp = wallet.recent_top_ups && wallet.recent_top_ups[0];
  if (latestTopUp && latestTopUp.status === 'failed') {
    return { label: 'Top-up failed', color: 'error' };
  }
  const { balance_cents, auto_top_up_threshold_cents } = wallet.wallet;
  const label = `$${(balance_cents / 100).toFixed(2)}`;
  if (balance_cents >= auto_top_up_threshold_cents) {
    return { label, color: 'success' };
  }
  return { label, color: 'warning' };
}

function EVCharging() {
  const navigate = useNavigate();
  const classes = useStyles();
  const api = useAPI();
  const [enrollment, setEnrollment] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const status = await EVChargingService.getEnrollment();
      setEnrollment(status);
      // Best-effort wallet fetch; only meaningful once enrolled. Failure
      // is silent (the wallet chip just doesn't render).
      if (status?.enrolled) {
        try {
          const w = await EVChargingService.getWallet();
          setWallet(w);
        } catch (_err) {
          // ignore
        }
      } else {
        setWallet(null);
      }
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          'Unable to load EV Charging enrollment status.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    mixpanel.track('EV_CHARGING_MENU');
  }, []);

  useEffect(() => {
    if (api) {
      EVChargingService.init(api);
      loadStatus();
    }
  }, [api, loadStatus]);

  const handleCancelEnrollment = async () => {
    try {
      setCancelling(true);
      const status = await EVChargingService.cancelEnrollment();
      setEnrollment(status);
      setConfirmCancelOpen(false);
    } catch (err) {
      setError(
        err?.response?.data?.message || 'Unable to cancel enrollment.',
      );
    } finally {
      setCancelling(false);
    }
  };

  const go = (path) => () => navigate(path);

  const isActive =
    enrollment && enrollment.enrolled && enrollment.status === 'active';
  const isCancelled = enrollment && enrollment.status === 'cancelled';
  const tosAccepted = !!enrollment?.tos_accepted_at;
  const tosChip = tosChipProps(enrollment);
  const paymentChip = paymentChipProps(enrollment);
  const walletChip = walletChipProps(wallet);

  return (
    <AuthenticatedContainer>
      <GridItem xs={12} sm={12} md={8} align='center'>
        <Typography variant='h5' style={{ color: '#324152' }}>
          EV CHARGING
        </Typography>
      </GridItem>

      {loading && (
        <GridItem xs={12} sm={12} md={8} align='center'>
          <Box sx={{ py: 4 }}>
            <CircularProgress />
          </Box>
        </GridItem>
      )}

      {error && (
        <GridItem xs={12} sm={12} md={8} align='center'>
          <Alert severity='error' sx={{ mt: 2, maxWidth: 360, mx: 'auto' }}>
            {error}
          </Alert>
        </GridItem>
      )}

      {!loading && enrollment && (
        <GridItem xs={12} sm={12} md={8} align='center'>
          <Box sx={{ maxWidth: 360, mx: 'auto', mt: 2 }}>
            {isActive && (
              <Box sx={{ textAlign: 'left' }}>
                <Chip label='Enrolled' color='success' size='small' sx={{ mb: 1 }} />
                {enrollment.card && (
                  <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                    Card on file: {formatBrand(enrollment.card.brand)} ending in{' '}
                    {enrollment.card.last4}
                  </Typography>
                )}
              </Box>
            )}
            {isCancelled && (
              <Box sx={{ textAlign: 'left' }}>
                <Chip label='Cancelled' size='small' sx={{ mb: 1 }} />
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  Re-enroll any time to start charging again.
                </Typography>
              </Box>
            )}
            {!isActive && !isCancelled && (
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  Enroll to charge your EV at any of our locations. We use a
                  pre-paid charging wallet topped up from your saved card;
                  each session is deducted from that balance.
                </Typography>
              </Box>
            )}
          </Box>
        </GridItem>
      )}

      <GridItem xs={12} sm={12} md={8} align='center'>
        <div className={classes.root}>
          <List>
            <ListItemButton onClick={go('/ev-charging/terms')}>
              <ListItemIcon>
                <GavelIcon />
              </ListItemIcon>
              <ListItemText primary='EV Charging TOS' />
              {tosChip && (
                <Chip
                  label={tosChip.label}
                  color={tosChip.color}
                  size='small'
                  sx={{ ml: 1 }}
                />
              )}
            </ListItemButton>
            <Divider />
            <ListItemButton
              onClick={go('/ev-charging/payment')}
              disabled={!tosAccepted}
            >
              <ListItemIcon>
                <CreditCardIcon />
              </ListItemIcon>
              <ListItemText
                primary={isActive ? 'Replace Card' : 'Payment Method'}
              />
              {paymentChip && (
                <Chip
                  label={paymentChip.label}
                  color={paymentChip.color}
                  size='small'
                  sx={{ ml: 1 }}
                />
              )}
            </ListItemButton>
            {isActive && (
              <>
                <Divider />
                <ListItemButton onClick={go('/ev-charging/wallet')}>
                  <ListItemIcon>
                    <AccountBalanceWalletIcon />
                  </ListItemIcon>
                  <ListItemText primary='Wallet' />
                  {walletChip && (
                    <Chip
                      label={walletChip.label}
                      color={walletChip.color}
                      size='small'
                      sx={{ ml: 1 }}
                    />
                  )}
                </ListItemButton>
                <Divider />
                <ListItemButton onClick={() => setConfirmCancelOpen(true)}>
                  <ListItemIcon>
                    <CancelIcon />
                  </ListItemIcon>
                  <ListItemText primary='Cancel Enrollment' />
                </ListItemButton>
              </>
            )}
          </List>
          <Divider />
        </div>
      </GridItem>

      <Dialog
        open={confirmCancelOpen}
        onClose={() => (cancelling ? null : setConfirmCancelOpen(false))}
      >
        <DialogTitle>Cancel EV Charging Enrollment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your saved card will no longer be auto-charged for EV Charging
            top-ups, and you will not be able to start new charging sessions.
            Any remaining balance in your charging wallet is preserved if you
            re-enroll later. Contact support if you would like a refund.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmCancelOpen(false)}
            disabled={cancelling}
          >
            Keep Enrolled
          </Button>
          <Button
            onClick={handleCancelEnrollment}
            color='error'
            disabled={cancelling}
          >
            {cancelling ? <CircularProgress size={18} /> : 'Cancel Enrollment'}
          </Button>
        </DialogActions>
      </Dialog>
    </AuthenticatedContainer>
  );
}

export default EVCharging;
